import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Container,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Grid,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  ArrowBack,
  Brightness4,
  Brightness7,
  Launch,
} from "@mui/icons-material";
import {
  DASHBOARD_CONFIG,
  generateMetabaseUrl,
  METABASE_SITE_URL,
  Dashboard,
} from "../config/metabase-config";



interface DashboardCardProps {
  dashboard: Dashboard;
  onSelect: (dashboard: Dashboard) => void;
  isDarkMode: boolean;
}

interface DashboardViewerProps {
  dashboard: Dashboard;
  onBack: () => void;
  isDarkMode: boolean;
}

interface DashboardListProps {
  onSelectDashboard: (dashboard: Dashboard) => void;
  isDarkMode: boolean;
}

type CategoryColors = {
  [key: string]: string;
};



// Dashboard Card Component
const DashboardCard: React.FC<DashboardCardProps> = ({
  dashboard,
  onSelect,
  isDarkMode,
}) => {
  const getCategoryColor = (category: string): string => {
    const colors: CategoryColors = {
      Sales: "#4caf50",
      Marketing: "#2196f3",
      Analytics: "#ff9800",
      Finance: "#9c27b0",
      Operations: "#f44336",
      Product: "#00bcd4",
      Errors: "#e53935", // red for errors
      Policies: "#3f51b5", // deep blue for policies
      Leads: "#ffc107", // amber/yellow for leads
    };
    return colors[category] || "#757575";
  };

  return (
    <Card
      elevation={3}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "all 0.3s ease",
        "&:hover": {
          elevation: 6,
          transform: "translateY(-4px)",
        },
        bgcolor: isDarkMode ? "#2d2d2d" : "white",
        color: isDarkMode ? "white" : "inherit",
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={2}
        >
          <Typography variant="h6" component="h2" gutterBottom>
            {dashboard.name}
          </Typography>
          <Chip
            label={dashboard.category}
            size="small"
            sx={{
              bgcolor: getCategoryColor(dashboard.category),
              color: "white",
              fontWeight: "bold",
            }}
          />
        </Box>

        <Typography
          variant="body2"
          color={isDarkMode ? "#ccc" : "text.secondary"}
          paragraph
        >
          {dashboard.description}
        </Typography>
      </CardContent>

      <CardActions sx={{ justifyContent: "space-between", px: 2, pb: 2 }}>
        <Typography
          variant="caption"
          color={isDarkMode ? "#999" : "text.secondary"}
        >
          ID: {dashboard.resourceId}
        </Typography>
        <Button
          variant="contained"
          startIcon={<DashboardIcon />}
          onClick={() => onSelect(dashboard)}
          sx={{
            bgcolor: getCategoryColor(dashboard.category),
            "&:hover": {
              bgcolor: getCategoryColor(dashboard.category),
              filter: "brightness(0.9)",
            },
          }}
        >
          View Dashboard
        </Button>
      </CardActions>
    </Card>
  );
};

// Individual Dashboard Viewer Component
const DashboardViewer: React.FC<DashboardViewerProps> = ({
  dashboard,
  onBack,
  isDarkMode,
}) => {
  const [dashboardUrl, setDashboardUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const loadDashboard = async (): Promise<void> => {
      try {
        setLoading(true);
        const url: string = await generateMetabaseUrl(
          "dashboard",
          dashboard.resourceId,
          dashboard.params,
          isDarkMode
        );
        if (url) {
          setDashboardUrl(url);
          setError(null);
        } else {
          setError("Failed to generate Metabase dashboard URL");
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "An unknown error occurred";
        setError(`Error: ${errorMessage}`);
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, [dashboard.resourceId, dashboard.params, isDarkMode]);

  return (
    <Box>
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          bgcolor: isDarkMode ? "#1e1e1e" : "white",
          color: isDarkMode ? "white" : "inherit",
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton onClick={onBack} color="primary">
              <ArrowBack />
            </IconButton>
            <Typography variant="h4" component="h1">
              {dashboard.name}
            </Typography>
            <Chip
              label={dashboard.category}
              color="primary"
              variant="outlined"
            />
          </Box>
        </Box>

        <Typography
          variant="body1"
          color={isDarkMode ? "#ccc" : "text.secondary"}
          paragraph
        >
          {dashboard.description}
        </Typography>

        {dashboardUrl && !loading && (
          <Typography
            variant="body2"
            color={isDarkMode ? "#999" : "text.secondary"}
            sx={{ mb: 2 }}
          >
            Dashboard URL:{" "}
            <Button
              href={dashboardUrl}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<Launch />}
              size="small"
            >
              Open in New Tab
            </Button>
          </Typography>
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Paper
          sx={{
            height: "600px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: isDarkMode ? "#1e1e1e" : "white",
            color: isDarkMode ? "white" : "inherit",
          }}
        >
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6">Loading Dashboard...</Typography>
          <Typography
            variant="body2"
            color={isDarkMode ? "#999" : "text.secondary"}
            sx={{ mt: 1 }}
          >
            Connecting to: {METABASE_SITE_URL}
          </Typography>
        </Paper>
      ) : dashboardUrl ? (
        <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
          <iframe
            src={dashboardUrl}
            frameBorder="0"
            style={{
              width: "100%",
              height: "600px",
              border: "none",
            }}
            allowFullScreen
            title={`${dashboard.name} Dashboard`}
          />
        </Paper>
      ) : (
        <Paper
          sx={{
            height: "600px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: isDarkMode ? "#1e1e1e" : "white",
            color: isDarkMode ? "white" : "inherit",
          }}
        >
          <Typography variant="h6">Failed to load dashboard</Typography>
        </Paper>
      )}
    </Box>
  );
};

// Main Dashboard List Component
const DashboardList: React.FC<DashboardListProps> = ({
  onSelectDashboard,
  isDarkMode,
}) => {
  const filteredDashboards: Dashboard[] = DASHBOARD_CONFIG;

  return (
    <Box>
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 4,
          bgcolor: isDarkMode ? "#1e1e1e" : "white",
          color: isDarkMode ? "white" : "inherit",
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom>
          Analytics Dashboards
        </Typography>
        <Typography
          variant="body1"
          color={isDarkMode ? "#ccc" : "text.secondary"}
        >
          Explore our collection of interactive dashboards for data-driven
          insights
        </Typography>
      </Paper>

      <Grid container spacing={4}>
        {filteredDashboards.map((dashboard: Dashboard) => (
          <Grid key={dashboard.id}>
            <DashboardCard
              dashboard={dashboard}
              onSelect={onSelectDashboard}
              isDarkMode={isDarkMode}
            />
          </Grid>
        ))}
      </Grid>

      {filteredDashboards.length === 0 && (
        <Paper
          sx={{
            p: 4,
            textAlign: "center",
            bgcolor: isDarkMode ? "#1e1e1e" : "white",
            color: isDarkMode ? "white" : "inherit",
          }}
        >
          <Typography variant="h6">No dashboards found</Typography>
          <Typography color={isDarkMode ? "#ccc" : "text.secondary"}>
            Try adjusting your search criteria
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

// Main App Component
const MetabaseDashboard: React.FC = () => {
  const [selectedDashboard, setSelectedDashboard] = useState<Dashboard | null>(
    null
  );
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);

  const handleSelectDashboard = (dashboard: Dashboard): void => {
    setSelectedDashboard(dashboard);
  };

  const handleBackToDashboards = (): void => {
    setSelectedDashboard(null);
  };

  const toggleDarkMode = (): void => {
    setIsDarkMode(!isDarkMode);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: isDarkMode ? "#121212" : "#f5f5f5",
        transition: "background-color 0.3s ease",
      }}
    >
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: isDarkMode ? "#1e1e1e" : "#1976d2",
          mb: 3,
        }}
      >
        <Toolbar>
          <DashboardIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Metabase Analytics
          </Typography>
          <IconButton color="inherit" onClick={toggleDarkMode}>
            {isDarkMode ? <Brightness7 /> : <Brightness4 />}
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl">
        {selectedDashboard ? (
          <DashboardViewer
            dashboard={selectedDashboard}
            onBack={handleBackToDashboards}
            isDarkMode={isDarkMode}
          />
        ) : (
          <DashboardList
            onSelectDashboard={handleSelectDashboard}
            isDarkMode={isDarkMode}
          />
        )}
      </Container>
    </Box>
  );
};

export default MetabaseDashboard;
